import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { Subject } from 'rxjs';
import {
	MeshStandardMaterial,
	NoColorSpace,
	RepeatWrapping,
	SRGBColorSpace,
	TextureLoader,
	Vector2,
} from 'three';

export interface DecorInfo {
	tileU_mm: number;
	tileV_mm: number;
	directional: boolean;
	grainAxis: 'U' | 'V' | 'NONE';
	maps: string[];
}

interface DecorManifest {
	key: string;
	decors: Record<string, DecorInfo>;
}

/** The MeshStandardMaterial slots a decor can fill. */
type DecorMapSlot = 'map' | 'roughnessMap' | 'normalMap' | 'bumpMap';

// Unified texture root. Kept as constants so the asset store can later move to a
// CDN/Vercel-Blob with a one-line change.
const TEXTURE_BASE = 'assets/media/textures';
const DECOR_BASE = `${TEXTURE_BASE}/decor`;
// Raw core-edge material (unfinished particleboard) shown where an edge has no
// edgeband, instead of the flat backend colour.
const EDGE_BASE = `${TEXTURE_BASE}/edge/particleboard`;
const ANISOTROPY = 16; // three clamps to the hardware maximum; keeps grain crisp at cabinet distance

/**
 * Loads the decor texture manifest and builds cached grain-ready
 * MeshStandardMaterials keyed by boardservice `externalLabel`.
 *
 * UVs are baked with the tiling repeat by the caller (see textureFace helper),
 * so every face of a decor can share one material — texture.repeat stays (1,1)
 * and RepeatWrapping does the tiling.
 */
@Injectable({ providedIn: 'root' })
export class DecorTextureService {
	private manifest?: DecorManifest;
	private readonly ready: Promise<void>;
	private readonly loader = new TextureLoader();
	private readonly materialCache = new Map<string, MeshStandardMaterial | null>();
	private coreEdgeMaterial?: MeshStandardMaterial;

	// Texture images load long after the material (and the mesh wearing it) exist.
	// Track the in-flight ones so callers can wait for a final, fully-textured
	// canvas — see whenSettled() and textureLoaded$.
	private pending = 0;
	private settledResolvers: Array<() => void> = [];
	private readonly textureLoaded = new Subject<void>();

	/**
	 * Emits whenever a texture finishes loading (or fails). three only uploads a
	 * texture to the GPU during a render pass, so a scene that has stopped
	 * rendering must draw another frame on this signal or the texture never
	 * appears.
	 */
	public readonly textureLoaded$ = this.textureLoaded.asObservable();

	constructor(@Inject(PLATFORM_ID) private readonly platformId: object) {
		this.ready = this.loadManifest();
	}

	private async loadManifest(): Promise<void> {
		// Decor textures are only used for 3D rendering (configurator/preview),
		// never on the prerendered public pages. Skip on the server so the build
		// makes no network call — the relative fetch below has no origin under
		// Node, and this stays safe even if DECOR_BASE later points at a CDN URL.
		if (isPlatformServer(this.platformId)) {
			this.manifest = { key: 'externalLabel', decors: {} };
			return;
		}
		// Plain fetch (not HttpClient) so this static asset skips the auth
		// interceptor — it loads at app-init, before login.
		try {
			const res = await fetch(`${DECOR_BASE}/manifest.json`, { cache: 'no-cache' });
			if (!res.ok) throw new Error(`HTTP ${res.status}`);
			this.manifest = await res.json();
			const n = Object.keys(this.manifest?.decors ?? {}).length;
			console.info(`[decor-texture] manifest loaded: ${n} decors`);
		} catch (e) {
			this.manifest = { key: 'externalLabel', decors: {} };
			console.warn('[decor-texture] manifest failed to load — decor textures disabled', e);
		}
	}

	/** Resolves once the manifest is available. */
	public whenReady(): Promise<void> {
		return this.ready;
	}

	/**
	 * Resolves once every texture requested so far has loaded or failed — i.e. no
	 * mesh is still wearing an empty texture. Callers must have finished building
	 * the scene first, since that is what requests the textures.
	 */
	public whenSettled(): Promise<void> {
		if (this.pending === 0) return Promise.resolve();
		return new Promise<void>((resolve) => this.settledResolvers.push(resolve));
	}

	public isReady(): boolean {
		return this.manifest !== undefined;
	}

	public hasDecor(externalLabel?: string): boolean {
		return !!externalLabel && !!this.manifest?.decors[externalLabel.toUpperCase()];
	}

	/**
	 * URL of the decor's colour map, usable as a board swatch when the board's
	 * own imageUrl can't be fetched. Null when there's no decor for this label.
	 */
	public getColorMapUrl(externalLabel?: string): string | null {
		if (!this.hasDecor(externalLabel)) return null;
		return `${DECOR_BASE}/${externalLabel!.toUpperCase()}/color.jpg`;
	}

	public getDecorInfo(externalLabel?: string): DecorInfo | undefined {
		if (!externalLabel) return undefined;
		return this.manifest?.decors[externalLabel.toUpperCase()];
	}

	/**
	 * A cached MeshStandardMaterial for the decor, or null if unknown. Reflection
	 * comes from the scene's environment map (set once in SceneManager); this
	 * material only carries the surface maps.
	 */
	public getDecorMaterial(externalLabel: string): MeshStandardMaterial | null {
		const label = externalLabel.toUpperCase();
		if (this.materialCache.has(label)) return this.materialCache.get(label)!;

		const info = this.manifest?.decors[label];
		if (!info) {
			this.materialCache.set(label, null);
			return null;
		}

		const material = new MeshStandardMaterial({
			metalness: 0.0,
			roughness: 1.0,
			envMapIntensity: 0.85,
		});
		const has = (m: string) => info.maps.includes(m);
		if (has('color.jpg')) this.load(material, 'map', label, 'color.jpg', true);
		if (has('roughness.jpg')) this.load(material, 'roughnessMap', label, 'roughness.jpg', false);
		if (has('normal.jpg')) {
			this.load(material, 'normalMap', label, 'normal.jpg', false);
			material.normalScale = new Vector2(1.4, 1.4);
		} else if (has('bump.jpg')) {
			this.load(material, 'bumpMap', label, 'bump.jpg', false);
			// Non-directional (unicolour) decors tile the relief finely (see the
			// builder), so the bump slopes are much steeper — keep the scale low so
			// the fine matte structure reads without darkening the surface.
			material.bumpScale = info.grainAxis === 'NONE' ? 0.2 : 1.5;
		}

		this.materialCache.set(label, material);
		return material;
	}

	private load(material: MeshStandardMaterial, slot: DecorMapSlot, label: string, file: string, srgb: boolean): void {
		this.loadUrl(material, slot, `${DECOR_BASE}/${label}/${file}`, srgb);
	}

	/**
	 * Fill one map slot. The Texture is assigned straight away but its image
	 * arrives later, so the load is counted (whenSettled) and announced
	 * (textureLoaded$) — a mesh wearing a texture that has no image yet samples as
	 * black, not as its flat colour.
	 */
	private loadUrl(material: MeshStandardMaterial, slot: DecorMapSlot, url: string, srgb: boolean): void {
		this.pending++;
		const tex = this.loader.load(
			url,
			() => this.settle(),
			undefined,
			() => {
				// Nothing will ever fill this texture, so leaving it assigned would
				// leave the surface black forever. Drop it back to the flat colour.
				console.warn(`[decor-texture] ${url} failed to load — dropping ${slot}`);
				material[slot] = null;
				material.needsUpdate = true;
				this.settle();
			},
		);
		tex.wrapS = tex.wrapT = RepeatWrapping;
		tex.colorSpace = srgb ? SRGBColorSpace : NoColorSpace;
		tex.anisotropy = ANISOTROPY;
		material[slot] = tex;
	}

	private settle(): void {
		this.pending--;
		this.textureLoaded.next();
		if (this.pending === 0) {
			const resolvers = this.settledResolvers;
			this.settledResolvers = [];
			resolvers.forEach((resolve) => resolve());
		}
	}

	/**
	 * Shared material for raw (un-edgebanded) core edges — unfinished
	 * particleboard, applied by the builder where an edge would otherwise render
	 * the flat backend colour.
	 */
	public getCoreEdgeMaterial(): MeshStandardMaterial {
		if (this.coreEdgeMaterial) return this.coreEdgeMaterial;
		const material = new MeshStandardMaterial({ metalness: 0.0, roughness: 1.0, envMapIntensity: 0.85 });
		this.loadUrl(material, 'map', `${EDGE_BASE}/color.jpg`, true);
		this.loadUrl(material, 'roughnessMap', `${EDGE_BASE}/roughness.jpg`, false);
		this.loadUrl(material, 'normalMap', `${EDGE_BASE}/normal.jpg`, false);
		material.normalScale = new Vector2(1, 1);
		this.coreEdgeMaterial = material;
		return material;
	}
}
